import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ChangeDetectorRef, NgZone } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  MOMENT_DATE_FORMAT_DASH_REVERSED,
  MOMENT_DATE_TIME_FORMAT,
  MOMENT_DATE_TIME_SECONDS_FORMAT,
  NAME_ANONYMIZE,
  NOTIFICATION_EMAILS_CONSTANTS,
  PAGING_HEADERS,
  ServiceType,
} from '@app/app.constants';
import { Sample } from '@app/modules/service-data/types/sample.types';
import {
  DocumentDescription,
  DocumentKeywords,
  DocumentType,
  ExtendedProductInventoryItem,
  NonOfficialDocumentReportTypes,
  ProductInventoryItemStatus,
} from '@app/modules/service-data/types/service-data.types';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import * as FileSaver from 'file-saver';
import JSZip from 'jszip';
import moment, { Moment } from 'moment';
import { Observable, concat, of } from 'rxjs';
import { catchError, map, toArray } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import {
  AnalysisType,
  AppInfo,
  BundleWithProducts,
  DsvCategory,
  DsvLimits,
  LogoPair,
  PaginationAndOrdering,
  Product,
  ProductInventory,
  Properties,
  RecipientModel,
  Run,
} from '../core.types';

export class Util {
  static RemoveEmptyProperties(obj: any) {
    Object.keys(obj).forEach((key) =>
      obj[key] === null || obj[key] === undefined ? delete obj[key] : {}
    );
    return obj;
  }

  static HasProperties(obj: any, properties: string[]) {
    return properties.every((key) =>
      Object.prototype.hasOwnProperty.call(obj, key)
    );
  }

  static PropertiesToObject(properties: Properties[] = []): any {
    const obj = {};
    for (const property of properties) {
      obj[property.key?.toLowerCase()] = property.value;
    }
    return obj;
  }

  static GetPropertyByKey(properties: Properties[] = [], key: string): string {
    return properties.find((p) => p.key?.toLowerCase() === key)?.value;
  }

  static CreateGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;

      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /*
   * Marks all controls in a form group as touched
   * @param formGroup - The group to clear
   */
  static MarkFormGroupTouched(formGroup: any) {
    Object.keys(formGroup.controls).map((control: any) => {
      formGroup.controls[control].markAsTouched();

      if (formGroup.controls[control].controls) {
        Util.MarkFormGroupTouched(formGroup.controls[control]);
      }
    });
  }

  static GetEmailPattern() {
    return '.+@.+\\..+';
  }

  static GetServiceDataRiskPattern() {
    return '^\\d+|^$';
  }

  static UpdateComponentView(
    cdr: ChangeDetectorRef,
    ngZone: NgZone,
    updateModelFunction: () => void
  ): Promise<void> {
    return ngZone.run(() => {
      updateModelFunction();
      cdr.markForCheck();
      return Promise.resolve();
    });
  }

  static GetHttpHeadersFromPaginationAndOrderingWithCT(
    listOptions?: PaginationAndOrdering
  ) {
    let headers = new HttpHeaders();
    if (listOptions) {
      if (listOptions.orderDirection) {
        headers = headers.append(
          PAGING_HEADERS.orderDirection,
          listOptions.orderDirection
        );
      }
      if (listOptions.orderBy) {
        headers = headers.append(PAGING_HEADERS.orderBy, listOptions.orderBy);
      }
      if (listOptions.top) {
        headers = headers.append(
          PAGING_HEADERS.top,
          listOptions.top.toString()
        );
      }
      if (listOptions.continuationToken) {
        headers = headers.append(
          PAGING_HEADERS.continuationToken,
          listOptions.continuationToken
        );
      }
    }
    return headers;
  }

  static ParseHttpHeadersResponseAndExtendResponse() {
    return map((response: any) => {
      const { headers } = response;

      if (headers) {
        let continuationToken = null;
        let totalCount = null;

        if (headers.has(PAGING_HEADERS.continuationToken)) {
          continuationToken = headers.get(PAGING_HEADERS.continuationToken);
        }

        if (headers.has(PAGING_HEADERS.totalCount)) {
          totalCount = parseInt(headers.get(PAGING_HEADERS.totalCount), 10);
        }

        return {
          ...{ data: response.body },
          continuationToken,
          totalCount,
        };
      }

      return { ...{ data: response.body } };
    });
  }

  static ConvertBase64ToBlob(base64: string): BlobPart {
    // decode base64 string, remove space for IE compatibility
    const binary = atob(base64.replace(/\s/g, ''));
    const len = binary.length;
    const buffer = new ArrayBuffer(len);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < len; i++) {
      view[i] = binary.charCodeAt(i);
    }

    return view;
  }

  static GetBase64BytesLength(base64: string): number {
    const binary = atob(base64.replace(/\s/g, ''));
    return binary.length;
  }

  static SaveDocxFile(base64: string, fileName: string) {
    const docxBlob = new Blob([Util.ConvertBase64ToBlob(base64)], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    FileSaver.saveAs(docxBlob, `${fileName}.docx`);
  }

  static SavePdfFile(base64: string, fileName: string) {
    const docxBlob = new Blob([Util.ConvertBase64ToBlob(base64)], {
      type: 'application/pdf',
    });
    FileSaver.saveAs(docxBlob, `${fileName}.pdf`);
  }

  static GetExtensionFromFileName(fileName: string): string {
    return fileName.substr(fileName.lastIndexOf('.') + 1);
  }

  static GetUrlFromBase64String(base64: string): string {
    const arrBuffer = Util.ConvertBase64ToBlob(base64);

    // It is necessary to create a new blob object with mime-type explicitly set
    // otherwise only Chrome works like it should
    const newBlob = new Blob([arrBuffer], { type: 'application/pdf' });

    // IE doesn't allow using a blob object directly as link href
    // instead it is necessary to use msSaveOrOpenBlob
    // removed with Angular 13 upgrade
    // if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    //   window.navigator.msSaveOrOpenBlob(newBlob);
    //   return;
    // }

    // For other browsers:
    // Create a link pointing to the ObjectURL containing the blob.
    return window.URL.createObjectURL(newBlob);

    // For directly downloading
    /*const link = document.createElement('a');
    document.body.appendChild(link); // required in FF, optional for Chrome
    link.href = data;
    link.download = 'file.pdf';
    link.click();
    window.URL.revokeObjectURL(data);
    link.remove();*/
  }

  static isEmpty = (value: any): boolean =>
    value == null ||
    (typeof value === 'object' && Object.keys(value).length === 0) ||
    (typeof value === 'string' && value.trim().length === 0);

  static getBase64ImageFromUrl(blob: Blob): Observable<string> {
    return new Observable((subscriber) => {
      const reader = new FileReader();
      reader.addEventListener(
        'load',
        () => {
          subscriber.next((reader.result as string).split(',')[1]);
          subscriber.complete();
        },
        false
      );

      reader.onerror = () => {
        subscriber.error(this);
        subscriber.complete();
      };
      reader.readAsDataURL(blob);
    });
  }

  static checkIfBlobExist(httpCLient: HttpClient, uri: string) {
    return httpCLient
      .get(uri, { responseType: 'blob' })
      .pipe(catchError(() => of(null)))
      .toPromise();
  }

  static getBase64ImageFromFile(file: File): Observable<any> {
    return new Observable((subscriber) => {
      const reader = new FileReader();
      reader.addEventListener(
        'load',
        () => {
          subscriber.next((reader.result as string).split(',')[1]);
          subscriber.complete();
        },
        false
      );
      reader.onerror = () => {
        subscriber.error(this);
        subscriber.complete();
      };
      reader.readAsDataURL(file);
    });
  }

  static formatFilterDateString(dateString: string | Moment, dateOnly = false) {
    if (dateString) {
      const format = dateOnly
        ? MOMENT_DATE_FORMAT_DASH_REVERSED
        : MOMENT_DATE_TIME_SECONDS_FORMAT;
      return moment(dateString, MOMENT_DATE_TIME_FORMAT).format(format);
    }
    return '';
  }

  static formatFilterDateMoment(m: moment.Moment, dateOnly = false): string {
    if (m) {
      const format = dateOnly
        ? MOMENT_DATE_FORMAT_DASH_REVERSED
        : MOMENT_DATE_TIME_SECONDS_FORMAT;
      return m.format(format);
    }
    return '';
  }

  static anyItemLoaded(items: any[]): boolean {
    return items && (!items.length || items.some((item) => !!item));
  }

  static createJsZip(
    folderName: string,
    files: {
      fileName: string;
      content: string;
      suffix?: string;
      options?: any;
    }[],
    fileSuffix = 'html'
  ): Promise<Blob> {
    const jsZip = new JSZip();
    files.forEach((f) => {
      const fileName = folderName ? `${folderName}\\${f.fileName}` : f.fileName;
      const suffix = f.suffix ? f.suffix : fileSuffix;
      jsZip.file(
        `${fileName}.${suffix}`,
        f.content,
        f.options ? f.options : {}
      );
    });
    return jsZip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
    });
  }

  static async downloadHtmlRequestForms(
    folderName: string,
    files: { fileName: string; content: string }[]
  ) {
    const zipArchive = await Util.createJsZip(folderName, files);
    FileSaver.saveAs(zipArchive, `request_forms_${folderName}.zip`);
  }

  static async downloadPdfRequestForms(
    folderName: string,
    files: { fileName: string; content: string }[]
  ) {
    const zipArchive = await Util.createJsZip(
      folderName,
      files.map((f) => ({
        ...f,
        options: {
          base64: true,
        },
      })),
      'pdf'
    );
    FileSaver.saveAs(zipArchive, `request_forms_${folderName}.zip`);
  }

  static loadLogo(): LogoPair {
    if (window.location.href.indexOf('stg') > -1) {
      return {
        large: 'assets/images/geneplanet-logo-stg.svg',
        small: 'assets/images/geneplanet-textless-logo.svg',
      };
    } else {
      return {
        large: 'assets/images/geneplanet-logo.svg',
        small: 'assets/images/geneplanet-textless-logo.svg',
      };
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  static chunkArray<T extends any>(myArray: T[], chunkSize: number): T[][] {
    const arrayLength = myArray.length;
    const tempArray = [];

    for (let index = 0; index < arrayLength; index += chunkSize) {
      const myChunk = myArray.slice(index, index + chunkSize);
      // Do something if you want with the group
      tempArray.push(myChunk);
    }

    return tempArray;
  }

  static removeDuplicates(myArray: string[]): string[] {
    return [...new Set(myArray)];
  }

  static getReportTypeFromName(fn: string): string {
    const fileName = fn.toLowerCase();
    if (
      fileName.includes(DocumentKeywords.highRisk1) ||
      fileName.includes(DocumentKeywords.highRisk2)
    ) {
      return DocumentType.HighRisk;
    } else if (fileName.includes(DocumentKeywords.delay)) {
      return DocumentType.Delay;
    } else if (
      fileName.includes(DocumentKeywords.resample1) ||
      fileName.includes(DocumentKeywords.resample2)
    ) {
      return DocumentType.Resample;
    } else if (fileName.includes(DocumentKeywords.refund)) {
      return DocumentType.Refund;
    } else if (fileName.includes(DocumentKeywords.notqualified)) {
      return DocumentType.NotQualified;
    } else if (fileName.includes(DocumentKeywords.canceled)) {
      return DocumentType.Canceled;
    } else if (fileName.includes(DocumentKeywords.nocall)) {
      return DocumentType.NoCall;
    } else if (fileName.includes(DocumentKeywords.official)) {
      return DocumentType.OfficialReport;
    }
  }

  static getReportResultFromName(fn: string): string {
    const fileName = fn.toLowerCase();
    const start = fileName.indexOf('-reportresult-');
    if (start >= 0) {
      return fn.substring(start).split('-')[2];
    }
    return null;
  }

  static getDocumentDescriptionFromName(fn: string): string {
    const fileName = fn.toLowerCase();
    if (fileName.includes(DocumentKeywords.notqualifiedbylaboratory)) {
      return DocumentDescription.ByLaboratory;
    } else if (fileName.includes(DocumentKeywords.notqualifiedbypartner)) {
      return DocumentDescription.ByPartner;
    }

    return '';
  }

  // split string by document type and then split it by -. Document reason is either right next to type or offset by one depending on document type
  static getDocumentReason(fn: string): string {
    const fnLowerCase = fn.toLowerCase();

    if (fn.includes(DocumentType.NotQualified)) {
      const halfSplit = fn.split(DocumentType.NotQualified + '-');
      return halfSplit[1].split('-')[1].split('_')[0];
    }

    if (fn.includes(DocumentType.Canceled)) {
      const halfSplit = fn.split(DocumentType.Canceled + '-');
      return halfSplit[1].split('-')[0].split('_')[0];
    }

    if (
      fnLowerCase.includes(DocumentKeywords.highRisk1) ||
      fnLowerCase.includes(DocumentKeywords.highRisk2)
    ) {
      const unifiedString = fnLowerCase.replace(
        DocumentKeywords.highRisk2,
        DocumentKeywords.highRisk1
      );

      const halfSplit = unifiedString.split(DocumentKeywords.highRisk1);
      const reasonsSplit = halfSplit[1].split('-');
      reasonsSplit.shift();
      return reasonsSplit
        .map((reason: string) => reason.split('_')[0].toUpperCase())
        .join(', ');
    }

    if (fnLowerCase.includes(DocumentKeywords.refund)) {
      return DocumentType.NoCall;
    }

    return null;
  }

  static getAnalysisTypeFromName(fn: string): string {
    const fileName = fn.toLowerCase();
    for (const type of Object.values(AnalysisType)) {
      if (fileName.includes('analysistype-' + type.toLowerCase())) {
        return type;
      }
    }
  }

  static addAnalysisTypeToName(filename: string, analysisType): string {
    if (filename.toLowerCase().includes('-analysistype-')) {
      return filename;
    }

    const gpString = 'geneplanet';
    const gpEndLocation =
      filename.toLowerCase().indexOf(gpString) + gpString.length;
    if (!filename.toLowerCase().indexOf(gpString)) {
      return filename;
    }
    return [
      filename.slice(0, gpEndLocation),
      `-AnalysisType-${analysisType}-`,
      filename.slice(gpEndLocation),
    ]
      .join('')
      .replace('--', '-');
  }

  static transformBGIFilename(filename: string) {
    if (filename.toLowerCase().includes('_geneplanet')) {
      // Replace "re-sample/resampling" with "resample", and "high-risk" with "high risk", so it doesn't mess with filename transformation
      filename = filename
        .replace(
          new RegExp(DocumentKeywords.resample1, 'gi'),
          DocumentKeywords.resample2
        )
        .replace(
          new RegExp(DocumentKeywords.resample3, 'gi'),
          DocumentKeywords.resample2
        )
        .replace(
          new RegExp(DocumentKeywords.highRisk2, 'gi'),
          DocumentKeywords.highRisk1
        );

      const withoutExtension = filename.substring(0, filename.length - 4);

      const gpIndex = withoutExtension.toLowerCase().indexOf('_geneplanet');

      const beforeGp = withoutExtension.substring(0, gpIndex);
      const afterGp = withoutExtension.substring(gpIndex);

      const afterGpSplit = afterGp.split('-');

      // last index is always type, therefore pop()
      let type =
        afterGpSplit.length > 1
          ? afterGpSplit.pop().toLowerCase()
          : 'official report';

      // If Unknown type, we assume it's a type of "High risk"
      if (
        !NonOfficialDocumentReportTypes.some((reportType) =>
          type.includes(reportType)
        ) &&
        !type.includes(DocumentKeywords.official)
      ) {
        type = `${DocumentKeywords.highRisk1}-${type}`;
      }
      const fileExtension = filename.split('.').pop();

      return `${beforeGp}${afterGpSplit.join('-')}-${type}.${fileExtension}`
        .split('_')
        .join('-');
    }
    return filename;
  }
}

export const validatePassword = (c: UntypedFormControl) => {
  const PASSWORD_REGEXP = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}/;
  return !c.value || PASSWORD_REGEXP.test(c.value)
    ? null
    : {
        passwordStrength: {
          valid: false,
        },
      };
};

export const getKitRuns = (kitId: string, runs: Run[]): Run[] => {
  if (kitId && runs) {
    return runs.filter(
      (r) => r.kits && r.kits.findIndex((k) => k.kitId === kitId) > -1
    );
  }
  return null;
};

export const getRunsKitsIdsUnion = (runs: Run[]): string[] =>
  runs
    .reduce(
      (arr, run) =>
        run.kits ? [...arr, ...run.kits.map((k) => k.kitId)] : arr,
      []
    )
    .filter(
      (kitId: string, index: number, arr: string[]) =>
        arr.indexOf(kitId) === index
    );

export const getRunsKitsIdsCrossSection = (runs: Run[]): string[] => {
  const kitIds = [];

  if (runs?.length) {
    runs.forEach((run: Run) => {
      kitIds.push(...run.kits.map((kit) => kit.kitId));
    });
  }

  return [...new Set(kitIds)];
};

export const sortRunByDateGenerated = (runs: Run[]) => {
  if (!runs || !runs.length) {
    return runs;
  }
  return runs
    .filter((run) => !!run)
    .sort((a, b) =>
      moment(a.generated.timestamp).isBefore(moment(b.generated.timestamp))
        ? 1
        : -1
    );
};

export const getPartnerServiceName = (products: Product[]) =>
  products?.[0]?.brand || ServiceType.NIPT;

export const getRecipientsString = (recipients: RecipientModel[]) => {
  if (recipients && recipients.length) {
    return recipients.map((r) => r.recipient).join(', ');
  }
  return '';
};

export const replaceNotificationEmailTemplateTerms = (
  term: string,
  serviceName: string,
  kitId: string,
  highRiskReason: string
): string => {
  term = term
    .split(NOTIFICATION_EMAILS_CONSTANTS.serviceNameReplaceString)
    .join(serviceName);
  term = term
    .split(NOTIFICATION_EMAILS_CONSTANTS.barcodeReplaceString)
    .join(kitId);
  term = term
    .split(NOTIFICATION_EMAILS_CONSTANTS.highRiskReasonReplaceString)
    .join(highRiskReason);
  return term;
};

export const isBMIOver40 = (weight: number, height: number): boolean => {
  const bmi = calculateBMI(weight, height);
  if (bmi > 40) {
    return true;
  }
  return false;
};

export const calculateBMI = (weight: number, height: number): number => {
  const heightInMeters = height / 100;
  return weight / (heightInMeters * heightInMeters);
};

export const mergeCountryFilter = (
  roleCountries: string[],
  countries: string[]
): string => {
  const selectedCountries: string[] = [];
  if (countries && countries.length) {
    for (let country of countries) {
      country = country.toLowerCase();
      if (!roleCountries || !roleCountries.length) {
        return countries.join(',');
      } else if (roleCountries.length && roleCountries.indexOf(country) > -1) {
        selectedCountries.push(country);
      }
      return selectedCountries.join(',');
    }
  } else if (roleCountries && roleCountries.length) {
    return roleCountries.join(',');
  }
  return null;
};

export const roundToTwoDecimals = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

export const parseNumber = (value: any): number => {
  if (value) {
    const numberValue = value * 1;
    if (!isNaN(numberValue)) {
      return numberValue;
    }
  }
  return 0;
};

export const readXslxFromBase64 = async (
  blobBase64: string,
  password?: string
) => {
  const wb: XLSX.WorkBook = XLSX.read(blobBase64, { type: 'base64', password });
  const wsName: string = wb.SheetNames[0];
  const ws: XLSX.WorkSheet = wb.Sheets[wsName];
  const sheetData = XLSX.utils.sheet_to_json(ws, {
    header: 1,
    defval: null,
  });
  return sheetData as any[][];
};

export const performSequentialCommands = (commands: Observable<any>[]) => {
  if (!commands || commands.length === 0) {
    return of([]);
  }
  return concat(...commands).pipe(toArray());
};

export const getSafeUTCDate = (date: Moment): Moment => {
  if (date && moment.isMoment(date)) {
    return moment.utc(date.format(MOMENT_DATE_FORMAT_DASH_REVERSED) + 'T12:00');
  }
  return date;
};

export const removeFiltersByKeys = (filters: any, keys: string[]): any => {
  const objecKeys = Object.keys(filters);
  if (filters && objecKeys.length > 0 && keys && keys.length > 0) {
    const filterKeysToRemove = objecKeys.filter((f) =>
      keys.find((k) => k === f)
    );
    for (const removeKey of filterKeysToRemove) {
      delete filters[removeKey];
    }
  }
  return filters;
};

export const getFileNameWithoutExtension = (fileName: string): string => {
  return fileName.replace('.docx', '').replace('.doc', '').replace('.pdf', '');
};

export const getAppInfoDisplayString = (appInfo: AppInfo): string => {
  if (appInfo) {
    return `${appInfo.tag} (${appInfo.hash})`;
  }
  return '';
};

export const isNumber = (value: any): boolean => !isNaN(toInteger(value));

export const toInteger = (value: any): number => parseInt(value, 10);

export const checkWindowLocationUrl = (): boolean => {
  if (
    window.location.href.indexOf('localhost') > -1 ||
    window.location.href.indexOf('-stg') > -1
  ) {
    return true;
  } else {
    return false;
  }
};

export const calculateSimpleCode = (kitId: string): string => {
  const simpleCode = kitId.replace(/[^0-9]/g, '');
  if (
    (simpleCode.length !== 9 && simpleCode.length !== 10) ||
    isNaN(+simpleCode) ||
    +simpleCode <= 206301322
  ) {
    return null;
  }
  const simpleCodeNumber = +simpleCode - 99998663;
  const hexSimpleCode = simpleCodeNumber.toString(16);
  return hexSimpleCode.toUpperCase();
};

export const anonymizeName = (name: string): string => {
  const names = name.toLowerCase().split(' ');
  const firstName = names.shift();
  const lastName = names.join(' ');

  return (
    (NAME_ANONYMIZE.firstNames[firstName[0]] ??
      NAME_ANONYMIZE.firstNames.default) +
    ' ' +
    (NAME_ANONYMIZE.lastNames[lastName[0]] ?? NAME_ANONYMIZE.lastNames.default)
  );
};

export const generatePassword = (
  length = 20,
  wishlist = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz~!@-#$'
) =>
  Array.from(crypto.getRandomValues(new Uint32Array(length)))
    .map((x) => wishlist[x % wishlist.length])
    .join('');

export const getHoursSinceVenipuncture = (
  dateOfVenipuncture: string | Date
) => {
  if (!dateOfVenipuncture) {
    return 0;
  }
  const endDate = moment();
  const diff = endDate.diff(moment(dateOfVenipuncture));
  return Math.floor(diff / 3600000);
};

export const getDaysSinceVenipuncture = (
  pii: ProductInventory,
  sample: Sample
) => {
  if (!sample?.dateOfVenipuncture) {
    return 0;
  }
  const endDate =
    pii?.lastStatus === ProductInventoryItemStatus.processingFinished ||
    pii?.lastStatus === ProductInventoryItemStatus.canceled ||
    pii?.lastStatus === ProductInventoryItemStatus.processingFailed
      ? moment(pii.lastStatusChanged)
      : moment();
  const diff = endDate.diff(moment(sample.dateOfVenipuncture));
  return Math.floor(diff / (24 * 3600000));
};

const getDsvLimits = (analysisType: AnalysisType): DsvLimits => {
  if (!analysisType) {
    return null;
  }

  switch (analysisType) {
    case AnalysisType.NIPT:
      return {
        delay: 14,
        processingStarted: 6,
        noDocumentsUpper: 10,
        noDocumentsLower: 7,
      };
    case AnalysisType.NIPPT:
      return {
        delay: 25,
        processingStarted: 15,
        noDocumentsUpper: 20,
        noDocumentsLower: 16,
      };
    case AnalysisType.PATERNITY:
      return {
        delay: 20,
        processingStarted: 10,
        noDocumentsUpper: 15,
        noDocumentsLower: 11,
      };
    case AnalysisType.MONO:
      return {
        delay: 28,
        processingStarted: 17,
        noDocumentsUpper: 22,
        noDocumentsLower: 18,
      };
    case AnalysisType.RH:
    case AnalysisType.CARRIER:
      return {
        delay: 15,
        processingStarted: 8,
        noDocumentsUpper: 12,
        noDocumentsLower: 9,
      };
    case AnalysisType.CANCERSCREEN:
      return {
        delay: 33,
        processingStarted: 20,
        noDocumentsUpper: 28,
        noDocumentsLower: 21,
      };
    case AnalysisType.VENISAFE:
      return {
        delay: 28,
        processingStarted: 20,
        noDocumentsUpper: 28,
        noDocumentsLower: 21,
      };
    case AnalysisType.VENISAFEMATERNA:
      return {
        delay: 15,
        processingStarted: 8,
        noDocumentsUpper: 12,
        noDocumentsLower: 9,
      };
    case AnalysisType.GALLERI:
      return {
        delay: 28,
        processingStarted: 17,
        noDocumentsUpper: 22,
        noDocumentsLower: 18,
      };
    case AnalysisType.CF:
      return {
        delay: 20,
        processingStarted: 16,
        noDocumentsUpper: 20,
        noDocumentsLower: 17,
      };
    case AnalysisType.SMA:
    case AnalysisType.FRAGILEX:
      return {
        delay: 12,
        processingStarted: 10,
        noDocumentsUpper: 12,
        noDocumentsLower: 10,
      };
    case AnalysisType.OTHER:
      return {
        delay: 28,
        processingStarted: 17,
        noDocumentsUpper: 22,
        noDocumentsLower: 18,
      };
    default:
      return null;
  }
};

export const getPiiDovCategory = (
  pii: ExtendedProductInventoryItem,
  dsv: number
): DsvCategory => {
  const delay = !!pii.properties.find((prop) => prop.key === 'Delay');
  const dsvLimits = getDsvLimits(pii.product?.analysisType);

  if (!dsvLimits) {
    return DsvCategory.none;
  }

  if (delay && dsv >= dsvLimits.delay) {
    return DsvCategory.red;
  }
  if (delay) {
    return DsvCategory.blue;
  }
  if (pii.lastStatus === ProductInventoryItemStatus.processingFailed) {
    return DsvCategory.gray;
  }
  if (pii.lastStatus === ProductInventoryItemStatus.canceled) {
    return DsvCategory.none;
  }

  if (pii.documents?.length === 0 && pii.reports?.length === 0) {
    if (dsv >= dsvLimits.noDocumentsUpper) {
      return DsvCategory.red;
    }
    if (dsv >= dsvLimits.noDocumentsLower) {
      return DsvCategory.orange;
    }
  }
  if (
    dsv >= 0 &&
    dsv <= dsvLimits.processingStarted &&
    pii.lastStatus === ProductInventoryItemStatus.processingStarted
  ) {
    return DsvCategory.green;
  }
  return DsvCategory.none;
};

export const noSort = () => {
  return 0;
};

export function groupBundlesByBrand(bundles: BundleWithProducts[]): {
  [brand: string]: BundleWithProducts[];
} {
  const brandGroups: { [key: string]: BundleWithProducts[] } = {};

  if (bundles?.length) {
    for (const bundle of bundles) {
      const productBrands = new Set(
        bundle.products.map((product) => product.brand)
      );

      if (productBrands.size === 1) {
        const brand = Array.from(productBrands)[0];
        if (!brandGroups[brand]) {
          brandGroups[brand] = [];
        }
        brandGroups[brand].push(bundle);
      } else {
        if (!brandGroups['MIXED']) {
          brandGroups['MIXED'] = [];
        }
        brandGroups['MIXED'].push(bundle);
      }
    }
  }

  return brandGroups;
}

export function momentToNgbDate(momentDate: moment.Moment): NgbDateStruct {
  return {
    year: momentDate.year(),
    month: momentDate.month() + 1,
    day: momentDate.date(),
  };
}

export function ngbDateToMoment(ngbDate: NgbDateStruct): moment.Moment {
  return moment({
    year: ngbDate.year,
    month: ngbDate.month - 1,
    day: ngbDate.day,
  });
}
